import { macroPackageRunCommandSchema, type MacroPackageRunReply } from '@playcastdotio/Messaging';
import type { NativeMacroPackageBridge } from '../../services/playjector/NativeMacroPackageBridge';

export const LOCAL_OWNED_NATIVE_ACTION = 'macroLocalOwnedNative';
interface OwnedRun { requestId: string; emit(reply: MacroPackageRunReply): void; draining: boolean }

/** Main-owned commands only; browser messages never enter this adapter. */
export class LocalOwnedNativeActionRouter {
  private readonly runs = new Map<string, OwnedRun>();
  constructor(private readonly native: Pick<NativeMacroPackageBridge, 'requestOwned' | 'cancel' | 'onReply' | 'onDisconnected'>) {
    native.onReply(reply => {
      const run = this.runs.get(reply.runId);
      if (!run || run.requestId !== reply.requestId || run.draining) return;
      run.emit(reply);
      this.settle(reply.runId, run, reply);
    });
    native.onDisconnected(() => { for (const run of this.runs.values()) run.draining = true; });
  }
  dispatch(value: unknown, emit: (reply: MacroPackageRunReply) => void): void {
    const command = macroPackageRunCommandSchema.parse(value);
    if (command.runId === '*') throw new Error('local_package_run_not_owned');
    if (command.command === 'start') {
      if (this.runs.has(command.runId) || [...this.runs.values()].some(run => run.draining)) throw new Error('local_package_run_not_owned');
      this.runs.set(command.runId, { requestId: command.requestId, emit, draining: false });
    }
    if ((command.command === 'cancel' || command.command === 'status') && !this.runs.has(command.runId)) throw new Error('local_package_run_not_owned');
    if (command.command === 'status' && this.runs.get(command.runId)?.draining) throw new Error('local_package_run_not_owned');
    void this.native.requestOwned(command).then(reply => {
      emit(reply);
      const run = this.runs.get(command.runId);
      if (run && (command.command === 'start' || command.command === 'cancel')) this.settle(command.runId, run, reply, command.command === 'cancel');
    }).catch(() => {
      const run = this.runs.get(command.runId);
      if (run && (command.command === 'start' || command.command === 'cancel')) run.draining = true;
      // The main channel waits for confirmed native replies and retains the original Stop owner.
    });
  }
  async release(): Promise<void> {
    await Promise.all([...this.runs].map(async ([runId, run]) => {
      run.draining = true;
      const result = await this.native.cancel(runId);
      if (result.outcome !== 'cancelled' || result.errorCode) throw new Error('input_release_unconfirmed');
      this.runs.delete(runId);
    }));
  }
  private settle(runId: string, run: OwnedRun, reply: MacroPackageRunReply, cancellation = false): void {
    if (cancellation ? reply.outcome !== 'cancelled' || !!reply.errorCode
      : ['input_release_unconfirmed', 'native_terminal_unconfirmed'].includes(reply.errorCode ?? '')) {
      run.draining = true; return;
    }
    if (['completed', 'cancelled', 'failed'].includes(reply.outcome)) this.runs.delete(runId);
  }
}
